import dynamic from 'next/dynamic';
import {
  useState, useRef, forwardRef, useEffect, useImperativeHandle,
} from 'react';
import { nanoid } from 'nanoid';
import GoogleMap from 'google-map-react';
import { Checkbox } from '@headlessui/react';
import { useSearchZustandStore } from '@/helpers/searchctx.js';
import googleOptions from './map/googleStyles';
import MapBuilder from './map/build.js';
import { debounceFunction } from '@/helpers/common';
import { useTranslation } from '@/helpers/translate';
import Emitter from '@/helpers/emitter.js';
import Icon from '@/core/icon.js';
import MarkerClusterer from '@/helpers/markerclusterplus.js';
import { getNewBounds } from '@/helpers/position.js';

const MapOptions = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState({
    run: true,
    changeText: 'search:map.runsearch',
    isLoadingOnClick: false,
    keyInput: nanoid(),
  });

  useImperativeHandle(ref, () => ({
    getRun: () => options?.run,
  }), [options]);

  const mapChanged = () => {
    if (!options.run) {
      setOptions((prev) => ({ ...prev, isLoadingOnClick: true, changeText: 'search:map.loading' }));
    }
  };

  useEffect(() => {
    Emitter.on('mapoptionschangedbymapchanged', mapChanged);
    return () => {
      Emitter.off('mapoptionschangedbymapchanged', mapChanged);
    };
  }, [options.run]);

  const initOptions = () => {
    setOptions({
      run: true,
      changeText: 'search:map.runsearch',
      isLoadingOnClick: false,
      keyInput: nanoid(),
    });
  };

  const handleCheckboxChange = (checked) => {
    setOptions((prev) => ({ ...prev, run: checked }));
  };

  const handleRefreshClick = (e) => {
    e.stopPropagation();
    initOptions();
    useSearchZustandStore.setState({ searchEvent: { action: 'mapforceRuncallsearch' } });
  };

  return (
    <div className="absolute invisible mt-6 transform -translate-x-1/2 md:visible z-5 left-1/2">
      <div className="flex justify-center">
        {options.isLoadingOnClick ? (
          <button
            className="flex items-center ml-2 text-sm bg-white shadow-button hover:bg-purple/10 hover:text-purple"
            onMouseUp={handleRefreshClick}
          >
            <span className="flex items-center px-4 py-2 group hover:text-purple">
              <Icon className="w-6 h-6 mr-2 text-black fill-current group-hover:text-purple" name="refresh" />
              {t(options.changeText)}
            </span>
          </button>
        ) : (
          <label className="flex items-center px-3 py-2 bg-white rounded-lg cursor-pointer shadow-button whitespace-nowrap">
            <Checkbox
              checked={options?.run}
              onChange={handleCheckboxChange}
              className={`size-5 inline-block items-center justify-center mr-2 rounded border-purple-4/20 hover:border-purple-5 focus:ring-0 focus:ring-offset-0 ${
                options?.run ? 'bg-blue border-blue' : 'bg-purple-4/20'
              }`}
            >
              {({ checked }) => (
                <svg
                  className={`p-0.5 size-5 text-white ${checked ? 'block' : 'hidden'}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              )}
            </Checkbox>
            <span className="cursor-pointer" onClick={() => handleCheckboxChange(!options?.run)}>{t(options.changeText)}</span>
          </label>
        )}
      </div>
    </div>
  );
});

const SearchMapContainer = forwardRef(({ districts }, ref) => {
  const { initParams } = useSearchZustandStore.getState();
  const mapOptionRef = useRef();
  const maxZoom = 17;

  const initialCenter = {
    lat: parseFloat(initParams?.lat) || 48.88566424963911,
    lng: parseFloat(initParams?.lng) || 2.37279135666447,
  };

  const initialZoom = !Number.isNaN(parseInt(initParams?.zoom, 10)) ? parseInt(initParams?.zoom, 10) : 12;

  const getBoundaries = (map) => {
    const bds = map.getBounds();
    return [
      [bds.getSouthWest().lat(), bds.getSouthWest().lng()],
      [bds.getNorthEast().lat(), bds.getNorthEast().lng()],
    ];
  };

  const initPolygons = (initdistricts) => {
    const newPolygons = [];
    initdistricts.forEach((d) => {
      if (d?.points) {
        const coordinates = d.points.map((c) => ({ lat: c[1], lng: c[0] }));
        const options = {
          paths: coordinates,
          strokeColor: '#865FFF',
          strokeOpacity: 0.9,
          strokeWeight: 1,
          fillColor: '#865FFF',
          fillOpacity: 0.05,
          _slugGZ: d?.slug,
        };

        const myPolygon = new google.maps.Polygon({ ...options });

        const handleMouseOver = () => {
          myPolygon.setOptions({ fillOpacity: 0.2, strokeWeight: 2 });
          Emitter.emit(`searchresultsspacehoversignalemittodistrict${d.slug}`, true);
        };

        const handleMouseOut = () => {
          myPolygon.setOptions({ fillOpacity: 0.05, strokeWeight: 1 });
          Emitter.emit(`searchresultsspacehoversignalemittodistrict${d.slug}`, false);
        };

        const handleClick = () => {
          Emitter.emit('search_map_build_moveMap', { zoom: 13, lat: d.center[1], lng: d.center[0] });
        };

        google.maps.event.addListener(myPolygon, 'mouseover', handleMouseOver);
        google.maps.event.addListener(myPolygon, 'mouseout', handleMouseOut);
        google.maps.event.addListener(myPolygon, 'click', handleClick);

        newPolygons.push({
          slug: d?.slug,
          item: myPolygon,
        });
      }
    });
    return newPolygons;
  };

  const debounceMapChange = debounceFunction((map) => {
    const { mapOutput } = useSearchZustandStore.getState();
    let action = 'mapidlecall';
    if ((mapOutput.zoom > 13 && map.zoom <= 13) || (mapOutput.zoom <= 13 && map.zoom > 13)) {
      action = 'mapidlecallclearmarkers';
    }
    useSearchZustandStore.setState({
      userUsingPlateform: nanoid(),
      currentPage: 1,
      mapEvent: {
        action,
        data: {
          lat: map.getCenter().lat(),
          lng: map.getCenter().lng(),
          zoom: map.zoom,
          boundaries: getBoundaries(map),
        },
        dontRun: !mapOptionRef?.current?.getRun(),
      },
    });
  }, 500);

  const createZoomControl = (controlDiv, map) => {
    controlDiv.style.padding = '5px';

    const controlWrapper = document.createElement('div');
    controlWrapper.classList.add('zoom');
    controlDiv.appendChild(controlWrapper);

    const zoomInButton = document.createElement('div');
    zoomInButton.classList.add('zoom-in');
    zoomInButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="1.4em" height="1.4em" viewBox="0 0 14 14"><path d="M6 6H1.007a1 1 0 100 2H6v4.993a1 1 0 102 0V8h4.993a1 1 0 100-2H8V1.007a1 1 0 10-2 0V6z"></path></svg>';
    controlWrapper.appendChild(zoomInButton);

    const separator = document.createElement('div');
    separator.classList.add('separator');
    controlWrapper.appendChild(separator);

    const zoomOutButton = document.createElement('div');
    zoomOutButton.classList.add('zoom-out');
    zoomOutButton.innerHTML = '<svg xmlns="http://www.w3.org/2000/svg" width="1.4em" height="1.4em" viewBox="0 0 1 1"><path d="M0.0719285714,0.428571429 C0.0324796607,0.428571429 0.0005,0.460551089 0.0005,0.5 C0.0005,0.539448911 0.0324796607,0.571428571 0.0719285714,0.571428571 L0.928071429,0.571428571 C0.967520339,0.571428571 0.9995,0.539448911 0.9995,0.5 C0.9995,0.460551089 0.967520339,0.428571429 0.928071429,0.428571429 L0.0719285714,0.428571429 Z"></path></svg>';
    controlWrapper.appendChild(zoomOutButton);

    zoomInButton.addEventListener('click', () => {
      map.setZoom(map.getZoom() + 1);
    });

    zoomOutButton.addEventListener('click', () => {
      map.setZoom(map.getZoom() - 1);
    });
  };

  const onGoogleApiLoaded = ({ map, maps }) => {
    const { mapRef } = useSearchZustandStore.getState();
    let currentZoom = initialZoom;

    if (!currentZoom
      && initParams.boundNElat && initParams.boundNElng
      && initParams.boundSWlat && initParams.boundSWlng) {
      const dim = mapRef?.current?.getBoundingClientRect?.();
      const bounds = getNewBounds({
        bounds: {
          ne: { lat: initParams.boundNElat, lng: initParams.boundNElng },
          sw: { lat: initParams.boundSWlat, lng: initParams.boundSWlng },
        },
        width: dim?.width,
        height: dim?.height,
      });

      if (bounds?.zoom) {
        currentZoom = bounds?.zoom || currentZoom;
      }
    }

    map.setZoom(currentZoom);

    map.setOptions({
      fullscreenControl: false,
      zoomControlOptions: {
        position: maps.ControlPosition.TOP_RIGHT,
      },
      styles: googleOptions,
      maxZoom,
      clickableIcons: false,
      disableDefaultUI: true,
    });

    const zoomControlDiv = document.createElement('div');
    createZoomControl(zoomControlDiv, map);

    zoomControlDiv.index = 1;
    map.controls[google.maps.ControlPosition.TOP_RIGHT].push(zoomControlDiv);

    google.maps.event.addListener(map, 'idle', () => {
      Emitter.emit('mapoptionschangedbymapchanged');
    });

    const styles = [
      { width: 0, height: 0, className: 'custom-clustericon-1' },
      { width: 40, height: 40, className: 'custom-clustericon-2' },
      { width: 50, height: 50, className: 'custom-clustericon-3' },
    ];

    const markerCluster = new MarkerClusterer(
      map,
      [],
      {
        styles,
        clusterClass: 'custom-clustericon',
      },
    );

    markerCluster.addListener('clusteringend', (cluster) => {
      if (!Object.entries(useSearchZustandStore.getState().clustersObject).length) {
        useSearchZustandStore.setState({ clustersObject: cluster });
      }
    });

    markerCluster.addListener('clusterclick', (cluster) => {
      const location = cluster.getBounds().getCenter();
      const latlng = new google.maps.LatLng(location.lat(), location.lng());

      // Déterminer le niveau de zoom approprié en fonction du nombre de marqueurs dans le cluster
      const markerCount = cluster.getMarkers().length;
      let targetZoom;

      if (map.zoom >= 17) {
        // Si on est déjà très zoomé, afficher les marqueurs individuels
        useSearchZustandStore.setState({
          pinEvent: {
            action: 'clusterclick',
            data: { markers: cluster.getMarkers(), latlng },
          },
        });
      } else {
        // En dehors des métropoles, zoomer moins agressivement
        if (markerCount > 50) {
          targetZoom = 12; // Pour les très grands clusters
        } else if (markerCount > 20) {
          targetZoom = 13; // Pour les clusters moyens
        } else if (markerCount > 10) {
          targetZoom = 14; // Pour les petits clusters
        } else {
          targetZoom = 15; // Pour les très petits clusters
        }

        Emitter.emit('search_map_build_moveMap', { zoom: targetZoom, latlng });
      }
    });

    google.maps.event.addListener(map, 'zoom_changed', () => {
      const { mapOutput } = useSearchZustandStore.getState();
      if (mapOutput.zoom !== map.zoom) {
        debounceMapChange(map);
      }
    });

    google.maps.event.addListener(map, 'dragend', () => {
      debounceMapChange(map);
    });

    google.maps.event.addListener(map, 'click', () => {
      useSearchZustandStore.setState({ pinIdOpen: null, pinData: null });
    });

    const boundaries = getBoundaries(map);

    useSearchZustandStore.setState({
      initializedMap: true,
      mapOutput: { boundaries, zoom: parseInt(currentZoom, 10), ...initialCenter },
      mapData: {
        map,
        markerCluster,
        polygons: [...initPolygons(districts)],
      },
      searchEvent: { action: 'mapinitDone' },
    });
  };

  return (
    <div className="w-full h-full lg:h-full" ref={ref}>
      <MapOptions ref={mapOptionRef} />
      <GoogleMap
        bootstrapURLKeys={{ key: process.env.NEXT_PUBLIC_GOOGLE_API_KEY }}
        center={initialCenter}
        onGoogleApiLoaded={onGoogleApiLoaded}
        yesIWantToUseGoogleMapApiInternals
        zoom={initialZoom || 12}
      />
      <MapBuilder />
    </div>
  );
});

export default SearchMapContainer;
